/** `<resource>/tag_categories/` + `<resource>/tag_options/` + `<resource>/tag_sources/`
 * — the tag autocompletes backing the shared TagConditionInput widget.
 *
 * The same three actions are mounted on every resource that owns a tag
 * condition (backend: `ConsoleTagLookupMixin`) so each is reachable with THAT
 * resource's own read permission. Deliberately NOT the tag-admin endpoints
 * (tagCategories.ts / tags.ts): those demand `console_tag_categories_view` /
 * `console_tags_view`, which a flag- or blacklist-managing user may lack.
 *
 * `makeTagLookups(basePath)` binds the set to one resource; the module-level
 * exports are the notification-features binding (the original consumer).
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules).
 */
import { apiGet } from './client';

const LIMIT = 20;

/** mode=codes — categories across all tenants deduped by code. */
export interface CategoryCodeRow {
  code: string;
  /** An arbitrary representative name (categories sharing the code differ). */
  name: string | null;
  /** Number of categories sharing the code. */
  tenants: number | null;
}

/** mode=concrete — one tenant's own categories. */
export interface TenantCategoryRow {
  id: number;
  code: string;
  name: string;
}

/** mode=names — live tag names for a category code, deduped across tenants. */
export interface TagNameRow {
  name: string;
  /** Tenants carrying the name. */
  count: number | null;
}

/** mode=canonicals — canonical tokens declared for a category code. */
export interface CanonicalRow {
  canonical: string;
  /** Aliases currently normalising to it — 0 is fine (a canonical is always
   *  its own implicit alias), but it is the "is this live?" signal. */
  alias_count: number | null;
}

/** mode=concrete — one category's own live tags. */
export interface CategoryTagRow {
  id: number;
  name: string;
  category: { id: number; code: string; name: string } | null;
  tenant: { id: number; code: string; name: string } | null;
}

/** One selectable tag source / match mode from the resource's registry. */
export interface TagSourceRow {
  codename: string;
  name: string;
  description?: string;
}

export interface TagRegistryResponse {
  tag_sources: TagSourceRow[];
  tag_match_modes: TagSourceRow[];
}

export interface TagLookups {
  registry(signal?: AbortSignal): Promise<TagRegistryResponse>;
  categoryCodes(q: string, signal?: AbortSignal): Promise<CategoryCodeRow[]>;
  tenantCategories(tenantId: number, q: string, signal?: AbortSignal): Promise<TenantCategoryRow[]>;
  tagsForCode(categoryCode: string, q: string, signal?: AbortSignal): Promise<TagNameRow[]>;
  canonicalsForCode(categoryCode: string, q: string, signal?: AbortSignal): Promise<CanonicalRow[]>;
  categoryTags(categoryId: number, q: string, signal?: AbortSignal): Promise<CategoryTagRow[]>;
}

/** Bind the three tag-lookup actions to one resource, e.g. '/blacklist_rules/'. */
export function makeTagLookups(basePath: string): TagLookups {
  const get = async <T>(action: string, usp: URLSearchParams, signal?: AbortSignal): Promise<T> =>
    apiGet<T>(`${basePath}${action}/?${usp.toString()}`, signal ? { signal } : undefined);

  return {
    async registry(signal) {
      return get<TagRegistryResponse>('tag_sources', new URLSearchParams(), signal);
    },
    async categoryCodes(q, signal) {
      const usp = new URLSearchParams({ mode: 'codes', limit: String(LIMIT) });
      if (q) usp.set('q', q);
      const res = await get<{ results: CategoryCodeRow[] }>('tag_categories', usp, signal);
      return res.results;
    },
    async tenantCategories(tenantId, q, signal) {
      const usp = new URLSearchParams({
        mode: 'concrete',
        tour_operator_id: String(tenantId),
        limit: String(LIMIT),
      });
      if (q) usp.set('q', q);
      const res = await get<{ results: TenantCategoryRow[] }>('tag_categories', usp, signal);
      return res.results;
    },
    async tagsForCode(categoryCode, q, signal) {
      const usp = new URLSearchParams({
        mode: 'names',
        category_code: categoryCode,
        limit: String(LIMIT),
      });
      if (q) usp.set('q', q);
      const res = await get<{ results: TagNameRow[] }>('tag_options', usp, signal);
      return res.results;
    },
    async canonicalsForCode(categoryCode, q, signal) {
      const usp = new URLSearchParams({
        mode: 'canonicals',
        category_code: categoryCode,
        limit: String(LIMIT),
      });
      if (q) usp.set('q', q);
      const res = await get<{ results: CanonicalRow[] }>('tag_options', usp, signal);
      return res.results;
    },
    async categoryTags(categoryId, q, signal) {
      const usp = new URLSearchParams({
        mode: 'concrete',
        category_id: String(categoryId),
        limit: String(LIMIT),
      });
      if (q) usp.set('q', q);
      const res = await get<{ results: CategoryTagRow[] }>('tag_options', usp, signal);
      return res.results;
    },
  };
}

/** Notification feature flags (`console_notification_features_view`). */
export const notificationFeatureTagLookups = makeTagLookups('/notification_features/');

/** Delivery blacklist rules (`console_blacklist_view`). */
export const blacklistTagLookups = makeTagLookups('/blacklist_rules/');

/** Notification rules editor (tpl-45 "tours matched by tag", CP-8264). Mounted
 *  on the rulesets viewset because its read actions open with ANY of the three
 *  editor permissions (rulesets/blocks/notifications view) — one binding serves
 *  both the notification editor and the ruleset editor. */
export const notificationRulesetTagLookups = makeTagLookups('/notification_rulesets/');

// Original module-level API, kept so existing callers are untouched.
export const categoryCodes = notificationFeatureTagLookups.categoryCodes;
export const tenantCategories = notificationFeatureTagLookups.tenantCategories;
export const tagsForCode = notificationFeatureTagLookups.tagsForCode;
export const canonicalsForCode = notificationFeatureTagLookups.canonicalsForCode;
export const categoryTags = notificationFeatureTagLookups.categoryTags;
